import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef } from 'react';

import { AuthField, AuthFieldRef } from '@/components/auth/AuthField';
import { HauserWordmark } from '@/components/dashboard/SidebarShell';
import { useTranslationPrefix } from '@/translations/i18n';

// Every signed-out screen (sign in, create account, password reset, verification, invitation) on one shell, ported from
// Tradehaus's sign-in (components/auth/AuthShell.jsx + styles/auth.module.css .split), with an abstract instrument
// field (see AuthField): the field fills the page, the brand line arrives a word at a time on
// the left, and a full-height frosted pane on the right holds the form.
// No blank screen, no floating dialog card. On a phone the field becomes a short band over the form.

const CSS = `
.hx-auth { position: fixed; inset: 0; overflow-y: auto; overflow-x: hidden; font-family: 'Manrope', sans-serif;
  --pane: clamp(400px, 34vw, 520px); --head-top: clamp(36px, 9vh, 96px); --head-size: clamp(26px, 3.2vw, 44px);
  --pane-pad: clamp(28px, 3.4vw, 52px);
  background: #bbb7af; color: #f7f7f6; }
/* the field sits on the brand's chalk, with a soft warm light from the upper left */
.hx-auth-field { position: absolute; inset: 0; overflow: hidden; background: radial-gradient(120% 90% at 30% 35%, #cdc9c1, #bbb7af 65%); }
.hx-auth-copy { position: absolute; top: var(--head-top); bottom: clamp(28px, 7vh, 72px); left: clamp(24px, 5vw, 64px);
  right: calc(var(--pane) + clamp(24px, 4vw, 56px)); display: flex; flex-direction: column; justify-content: space-between;
  gap: 32px; pointer-events: none; transition: opacity 0.5s ease, transform 0.6s ease; }
.hx-auth-headline { margin: 0; max-width: 560px; font-weight: 200; font-size: var(--head-size); letter-spacing: -0.05em;
  line-height: 0.98; color: #373736; }
.hx-auth-headline em { font-style: normal; color: rgba(55, 55, 54, 0.6); }
.hx-auth-word { display: inline-block; white-space: pre; animation: hxWordIn 0.9s cubic-bezier(0.22, 0.8, 0.3, 1) both;
  animation-delay: calc(0.25s + var(--w) * 0.12s); }
@keyframes hxWordIn { from { opacity: 0; transform: translateY(14px); filter: blur(8px); } }
.hx-auth-pane { position: absolute; top: 0; right: 0; bottom: 0; width: var(--pane); box-sizing: border-box;
  display: flex; flex-direction: column; justify-content: center; padding: 48px var(--pane-pad); z-index: 1;
  /* slate on the chalk (Hauser's tools material): near-solid, so the form keeps its contrast over the light ground */
  background: rgba(38, 38, 37, 0.94); -webkit-backdrop-filter: blur(20px) saturate(1.3); backdrop-filter: blur(20px) saturate(1.3);
  box-shadow: inset 1px 0 0 rgba(255, 255, 255, 0.06), -30px 0 60px -40px rgba(60, 52, 40, 0.5);
  transition: transform 0.7s cubic-bezier(0.6, 0, 0.2, 1), opacity 0.6s ease;
  animation: hxPaneIn 0.9s cubic-bezier(0.22, 0.8, 0.3, 1) both; }
@keyframes hxPaneIn { from { transform: translateX(40px); opacity: 0; } }
.hx-auth-card { width: 100%; max-width: 380px; margin: 0 auto; }
.hx-auth-logo { line-height: 0; margin-bottom: 40px; }
.hx-auth.leaving .hx-auth-pane { transform: translateX(105%); opacity: 0; }
.hx-auth.leaving .hx-auth-copy { opacity: 0; transform: translateY(-8px); }
/* the form's own type: a light display title, a muted line under it, quiet links */
.hx-auth-title { margin: 0 0 10px; font-size: 30px; font-weight: 300; letter-spacing: -0.04em; line-height: 1.15;
  text-wrap: balance; color: #f7f7f6; }
.hx-auth-sub { margin: 0 0 26px; font-size: 13.5px; line-height: 1.55; color: rgba(247, 247, 246, 0.6); }
.hx-auth-link { color: #f7f7f6; font-weight: 600; text-decoration: none; background: none; border: 0; padding: 0;
  font: inherit; cursor: pointer; }
.hx-auth-link:hover { text-decoration: underline; text-underline-offset: 3px; }
.hx-auth-error { margin: 0; padding: 12px 14px; border-radius: 12px; background: rgba(219, 82, 130, 0.12);
  color: #f7f7f6; font-size: 12.5px; line-height: 1.5; display: flex; gap: 10px; align-items: flex-start; }
.hx-auth-error::before { content: ''; flex: none; width: 2px; height: 14px; margin-top: 2px; border-radius: 1px; background: #DB5282; }
.hx-auth-foot { margin-top: 22px; font-size: 12.5px; color: rgba(247, 247, 246, 0.6); text-align: center; }
@media (max-width: 880px) {
  .hx-auth { display: flex; flex-direction: column; }
  .hx-auth-field { position: relative; inset: auto; height: 52vh; flex: none; width: 100%; }
  .hx-auth-copy { right: clamp(24px, 5vw, 64px); }
  .hx-auth-pane { position: relative; width: 100%; flex: 1; padding: 32px 20px 40px; box-shadow: none; }
}
@media (max-width: 600px) {
  .hx-auth { --head-top: 22px; --head-size: 26px; }
  .hx-auth-field { height: 30svh; min-height: 180px; max-height: 260px; }
  .hx-auth-copy { bottom: 16px; }
  .hx-auth-pane { padding: 26px 22px calc(28px + env(safe-area-inset-bottom, 0px)); justify-content: flex-start; }
}
@media (prefers-reduced-motion: reduce) { .hx-auth-word, .hx-auth-pane { animation: none; } .hx-auth-pane, .hx-auth-copy { transition: none; } }
`;

export interface AuthShellRef {
  /** the signed-in exit: the pane slides away and the field swings toward it, then fades */
  leave: () => Promise<void>;
}

export const AuthShell = forwardRef<
  AuthShellRef,
  {
    /** the browser tab's title */
    title: string;
    children: ReactNode;
    /** below the form: the switch to the other door ("No account? Create one") */
    footer?: ReactNode;
  }
>(function AuthShell({ title, children, footer }, ref) {
  const t = useTranslationPrefix('common');
  const field = useRef<AuthFieldRef>(null);
  const root = useRef<HTMLDivElement>(null);
  useImperativeHandle(
    ref,
    () => ({
      leave: async () => {
        root.current?.classList.add('leaving');
        await field.current?.leave();
      },
    }),
    [],
  );

  useEffect(() => {
    const prev = document.title;
    document.title = `${title} · Hauser`;
    return () => {
      document.title = prev;
    };
  }, [title]);

  // the brand line, a word at a time: "Homeownership," then the second clause in the quieter ink
  const words = [
    ...String(t('tagline.0')).trim().split(' ').map((w) => ({ w, em: false })),
    ...String(t('tagline.1')).trim().split(' ').map((w) => ({ w, em: true })),
  ];

  return (
    <div ref={root} className="hx-auth" data-field-host="">
      <style>{CSS}</style>
      <section className="hx-auth-field" aria-hidden>
        <AuthField ref={field} />
        <div className="hx-auth-copy">
          <h2 className="hx-auth-headline">
            {words.map(({ w, em }, i) => {
              const word = (
                <span key={i} className="hx-auth-word" style={{ ['--w' as string]: i }}>
                  {w}
                  {i < words.length - 1 && !(words[i + 1].em && !em) ? ' ' : ''}
                </span>
              );
              // the second clause ("made smarter.") starts its own line
              const breakBefore = em && i > 0 && !words[i - 1].em;
              return em ? (
                <span key={i}>
                  {breakBefore && <br />}
                  <em>{word}</em>
                </span>
              ) : (
                word
              );
            })}
          </h2>
        </div>
      </section>
      <div className="hx-auth-pane">
        <main className="hx-auth-card">
          <div className="hx-auth-logo">
            <HauserWordmark height={22} color="#f7f7f6" />
          </div>
          {children}
          {footer && <div className="hx-auth-foot">{footer}</div>}
        </main>
      </div>
    </div>
  );
});

export default AuthShell;
